import { useEffect, useState } from "react";
import { Activity } from "lucide-react";
import { toast } from "sonner";
import { API, BACKEND } from "@/lib/api";


export default function UptimePanel({ appId, app, checks: wsChecks, onSaved }) {
    const [checks, setChecks] = useState([]);
    const [summary, setSummary] = useState(null);
    const [url, setUrl] = useState(app?.config?.target_url || "");
    const [intervalS, setIntervalS] = useState(app?.config?.interval_s || 30);
    const [authHeader, setAuthHeader] = useState(app?.config?.auth_header || "");
    const [authValue, setAuthValue] = useState(app?.config?.auth_value || "");
    const [saving, setSaving] = useState(false);
    const configured = !!app?.config?.target_url;

    useEffect(() => {
        let dead = false;
        (async () => {
            try {
                const r = await fetch(`${API}/apps/${appId}/uptime`, { credentials: "include" });
                if (r.ok) {
                    const d = await r.json();
                    if (!dead) {
                        setChecks(d.checks || []);
                        setSummary(d.summary || null);
                    }
                }
            } catch {
                /* best-effort */
            }
        })();
        return () => {
            dead = true;
        };
    }, [appId]);

    useEffect(() => {
        if (!wsChecks?.length) return;
        setChecks((prev) => {
            const seen = new Set(prev.map((c) => c.id));
            const merged = [...wsChecks.filter((c) => !seen.has(c.id)), ...prev];
            return merged.slice(0, 120);
        });
    }, [wsChecks]);

    const save = async () => {
        setSaving(true);
        try {
            const r = await fetch(`${API}/apps/${appId}/config`, {
                method: "PATCH",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    config: {
                        target_url: url.trim(),
                        interval_s: Number(intervalS) || 30,
                        auth_header: authHeader.trim(),
                        auth_value: authValue.trim(),
                    },
                }),
            });
            if (!r.ok) throw new Error("failed");
            toast.success("Monitor configured", {
                description: "First ping lands within a few seconds.",
            });
            onSaved?.();
        } catch {
            toast.error("Could not save config");
        } finally {
            setSaving(false);
        }
    };

    if (!configured) {
        return (
            <div data-testid="rest-setup-card" className="glass-panel rounded-xl p-5">
                <h3 className="flex items-center gap-2 font-display text-sm font-bold tracking-tight text-slate-100">
                    <Activity className="h-4 w-4 text-cyan-400" />
                    Point MDH at your API
                </h3>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <input
                        data-testid="rest-target-url-input"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder="https://api.yourservice.com/health"
                        className="rounded-md border border-slate-800 bg-slate-950/70 px-3.5 py-2.5 font-mono text-xs text-slate-200 placeholder:text-slate-600 focus:border-cyan-500/60 focus:outline-none sm:col-span-2"
                    />
                    <div className="flex items-center gap-2">
                        <span className="shrink-0 font-mono text-[10px] text-slate-500">
                            ping every
                        </span>
                        <input
                            data-testid="rest-interval-input"
                            type="number"
                            min={10}
                            value={intervalS}
                            onChange={(e) => setIntervalS(e.target.value)}
                            className="w-24 rounded-md border border-slate-800 bg-slate-950/70 px-3 py-2 font-mono text-xs text-slate-200 focus:border-cyan-500/60 focus:outline-none"
                        />
                        <span className="font-mono text-[10px] text-slate-500">sec</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            data-testid="rest-auth-header-input"
                            value={authHeader}
                            onChange={(e) => setAuthHeader(e.target.value)}
                            placeholder="Header (optional)"
                            className="w-1/2 rounded-md border border-slate-800 bg-slate-950/70 px-3 py-2 font-mono text-xs text-slate-200 placeholder:text-slate-600 focus:border-cyan-500/60 focus:outline-none"
                        />
                        <input
                            data-testid="rest-auth-value-input"
                            value={authValue}
                            onChange={(e) => setAuthValue(e.target.value)}
                            placeholder="Value"
                            className="w-1/2 rounded-md border border-slate-800 bg-slate-950/70 px-3 py-2 font-mono text-xs text-slate-200 placeholder:text-slate-600 focus:border-cyan-500/60 focus:outline-none"
                        />
                    </div>
                </div>
                <button
                    data-testid="rest-save-config-button"
                    onClick={save}
                    disabled={saving || !url.trim()}
                    className="mt-4 rounded-md bg-cyan-400 px-5 py-2.5 text-xs font-bold text-slate-950 transition-colors hover:bg-cyan-300 disabled:opacity-50"
                >
                    {saving ? "Saving…" : "Start monitoring"}
                </button>
            </div>
        );
    }

    const recent = checks.slice(0, 60).reverse();
    const maxLat = Math.max(1, ...recent.map((c) => c.latency || 0));
    const points = recent
        .map(
            (c, i) =>
                `${(i / Math.max(1, recent.length - 1)) * 600},${76 - ((c.latency || 0) / maxLat) * 68}`,
        )
        .join(" ");

    return (
        <div data-testid="uptime-panel" className="glass-panel rounded-xl p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="flex items-center gap-2 font-display text-sm font-bold tracking-tight text-slate-100">
                    <Activity className="h-4 w-4 text-cyan-400" />
                    Uptime & latency
                    <span className="max-w-[280px] truncate font-mono text-[10px] font-normal text-slate-500">
                        {app.config.target_url}
                    </span>
                </h3>
                <span className="font-mono text-[10px] text-slate-500">
                    every {app.config.interval_s || 30}s
                </span>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                    ["Uptime", `${summary?.uptime ?? 100}%`, "text-emerald-300"],
                    ["Avg latency", `${summary?.avg_latency ?? 0}ms`, "text-slate-100"],
                    ["P99", `${summary?.p99 ?? 0}ms`, "text-slate-100"],
                    [
                        "Last check",
                        summary?.last_code === 599 ? "down" : summary?.last_code ?? "—",
                        summary?.last_code && summary.last_code < 400
                            ? "text-emerald-300"
                            : "text-rose-300",
                    ],
                ].map(([label, value, cls]) => (
                    <div
                        key={label}
                        className="rounded-lg border border-slate-800/80 bg-slate-950/50 px-4 py-3"
                    >
                        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">
                            {label}
                        </div>
                        <div className={`font-display mt-1 text-lg font-bold ${cls}`}>{value}</div>
                    </div>
                ))}
            </div>

            <div className="mt-4 rounded-lg border border-slate-800/80 bg-slate-950/50 p-3">
                {recent.length > 1 ? (
                    <svg
                        data-testid="uptime-sparkline"
                        viewBox="0 0 600 80"
                        preserveAspectRatio="none"
                        className="h-20 w-full"
                    >
                        <polyline
                            points={points}
                            fill="none"
                            stroke="#06B6D4"
                            strokeWidth="1.5"
                            strokeLinejoin="round"
                        />
                    </svg>
                ) : (
                    <p className="py-6 text-center font-mono text-[10px] text-slate-600">
                        First ping pending…
                    </p>
                )}
                <div className="mt-2 flex gap-0.5">
                    {checks.slice(0, 60).reverse().map((c) => (
                        <span
                            key={c.id}
                            title={`${c.code} · ${c.latency}ms`}
                            className={`h-3 flex-1 rounded-sm ${c.ok ? "bg-emerald-500/60" : "bg-rose-500/70"}`}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}
